// The wheel's colours, expressed in the same OKLCH the app's design tokens are
// written in (tailwind.config.js) and converted here rather than pasted in as
// hex. Two reasons: the numbers stay checkable against the token they came
// from, and the renderer needs *linear* RGB anyway — lighting maths only
// behaves (a surface at half brightness actually looking half lit) in linear
// space, and the shader encodes back to sRGB on the way out.

export type Rgb = readonly [number, number, number];

/**
 * One OKLCH token as linear sRGB, unclamped-then-clamped per channel.
 *
 * @param l lightness, 0–1, as written in the token
 * @param c chroma, as written in the token
 * @param h hue in degrees, as written in the token
 */
export function oklch(l: number, c: number, h: number): Rgb {
  const hr = (h * Math.PI) / 180;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);

  const lp = l + 0.3963377774 * a + 0.2158037573 * b;
  const mp = l - 0.1055613458 * a - 0.0638541728 * b;
  const sp = l - 0.0894841775 * a - 1.291485548 * b;

  const l3 = lp * lp * lp;
  const m3 = mp * mp * mp;
  const s3 = sp * sp * sp;

  const clamp = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
  return [
    clamp(4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3),
    clamp(-1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3),
    clamp(-0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3),
  ];
}

/**
 * Linear → sRGB, the same encode the shader does on the way out. Anything that
 * hands a colour to the browser rather than to the lighting maths — a CSS
 * string, a render pass's clear value — has to go through this first.
 */
export function encodeSrgb(rgb: Rgb): Rgb {
  const enc = (v: number) => {
    const c = Math.min(1, Math.max(0, v));
    return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  };
  return [enc(rgb[0]), enc(rgb[1]), enc(rgb[2])];
}

/** The same colour as a CSS string, for the 2D fallback painter and canvas textures. */
export function css(rgb: Rgb, alpha = 1): string {
  const [r, g, b] = encodeSrgb(rgb).map((v) => Math.round(v * 255));
  return alpha >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Mixes two linear colours — `t` = 0 is `a`, 1 is `b`. */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

// Tokens, straight from tailwind.config.js unless noted.
export const PALETTE = {
  /** `ink` — the dark pockets, and the wheel's outer body. */
  ink: oklch(0.16, 0.014, 75),
  inkDeep: oklch(0.1, 0.014, 75),
  /** `clay` — the rust pockets, alternating with ink the way red alternates
   *  with black on a real wheel. */
  clay: oklch(0.42, 0.16, 30),
  clayLit: oklch(0.5, 0.17, 33),
  /** `brass` — frets, the cone, the deflectors. The one warm metal. */
  brass: oklch(0.63, 0.145, 78),
  brassDeep: oklch(0.42, 0.11, 70),
  brassLit: oklch(0.8, 0.145, 88),
  /** The beer pocket: the wheel's zero, and the only gold field on it. */
  gold: oklch(0.78, 0.165, 90),
  goldDeep: oklch(0.58, 0.14, 86),
  /** `sage`, darkened — the felt the wheel stands on. */
  felt: oklch(0.38, 0.105, 152),
  feltDeep: oklch(0.24, 0.08, 154),
  /** `card` — the ball, and the face of each avatar chip. */
  bone: oklch(0.965, 0.008, 85),
  paper: oklch(0.91, 0.008, 95),
  paper2: oklch(0.865, 0.01, 95),
  line: oklch(0.66, 0.01, 90),
  ink3: oklch(0.54, 0.012, 75),

  // The wheel's own materials. These are the one part of the palette that
  // isn't a design token: a roulette wheel is a real object made of rosewood,
  // steel and lacquer, and the app's tokens — which are a paper-and-ink
  // scheme for cards and buttons — have no colours for any of it. Matched to
  // a photographed wheel instead, and kept here rather than in the mesh
  // builders so the two renderers can't drift apart.

  /** Rosewood: the bowl, the rim, the cone. Lit on the turned top edge where
   *  the polish catches the light, deep on the sides falling away from it. */
  rosewood: oklch(0.42, 0.085, 46),
  rosewoodLit: oklch(0.58, 0.1, 55),
  rosewoodDeep: oklch(0.26, 0.06, 40),
  /** The red pockets — a lacquer red, well clear of the rust `clay` is. */
  pocketRed: oklch(0.46, 0.185, 28),
  pocketRedDeep: oklch(0.3, 0.13, 27),
  /** The black pockets. Not actually black: a warm near-black, which is what
   *  keeps them reading as lacquer rather than as a hole in the wheel. */
  pocketBlack: oklch(0.2, 0.014, 60),
  pocketBlackDeep: oklch(0.12, 0.012, 60),
  /** Steel: the frets between the pockets, and the ring they stand on. */
  silver: oklch(0.78, 0.008, 250),
  silverLit: oklch(0.95, 0.005, 250),
  silverDeep: oklch(0.5, 0.01, 255),
} as const;
